import { useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  businessModulePath,
  jobCatalogue,
  officeCapabilityPath,
  resolveWorkMoment,
  roleDefinitions,
  rolePath,
  workMomentCatalogue,
  workMomentPath
} from "../catalogue";
import {
  CatalogueFacet,
  ModuleAgentSupervisionFrame,
  type SupervisionModeView,
  type SupervisionOutcomeState,
  type SupervisionRecordView
} from "../components/catalogue";
import { useCopy, useFramework } from "../framework";
import { PlaceTrail } from "../components/PlaceTrail";
import { WorkMomentIllustration } from "../components/WorkMomentIllustration";
import { AbsenceBlock, EmptyState, PageHeader, PlaceLine, StatusMark, humanize } from "../components/ui";

function replaceParam(params: URLSearchParams, key: string, value: string): URLSearchParams {
  const next = new URLSearchParams(params);
  if (value) next.set(key, value);
  else next.delete(key);
  return next;
}

const officeOptions = ["composer", "conductor"] as const;
const proofOptions = ["reference", "pattern", "roadmap"] as const;
const printModeOrder = ["supervise-work", "explore-evidence", "review-learning", "open-office-artifact"] as const;

function replaceCount(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

function replaceLabel(template: string, label: string): string {
  return template.replace("{label}", label);
}

function replaceValues(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (resolved, [key, value]) => resolved.replaceAll(`{${key}}`, String(value)),
    template
  );
}

export function WorkMomentsReferencePage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const [searchParams, setSearchParams] = useSearchParams();
  const [roleFacetOpen, setRoleFacetOpen] = useState(false);
  const [jobFacetOpen, setJobFacetOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const query = searchParams.get("q") ?? "";
  /* Role and job are true multi-select facets: the URL carries a comma list
   * so the control's selection model and the filter model agree. */
  const requestedRoleIds = (searchParams.get("role") ?? "").split(",").filter(Boolean);
  const requestedJobIds = (searchParams.get("job") ?? "").split(",").filter(Boolean);
  const requestedDomainId = searchParams.get("domain") ?? "";
  const requestedOffice = searchParams.get("office") ?? "";
  const requestedProof = searchParams.get("proof") ?? "";
  const roles = roleDefinitions(snapshot);
  const jobs = jobCatalogue(snapshot);
  const roleIds = new Set(roles.map(({ definition }) => definition.id));
  const jobIds = new Set(jobs.map(({ businessModule }) => businessModule.id));
  const domainIds = new Set(snapshot.content.domainWorkblocks.map(({ id }) => id));
  const selectedRoleIds = requestedRoleIds.filter((id) => roleIds.has(id));
  const selectedJobIds = requestedJobIds.filter((id) => jobIds.has(id));
  const domainId = requestedDomainId && domainIds.has(requestedDomainId) ? requestedDomainId : "";
  const office = requestedOffice && officeOptions.includes(requestedOffice as typeof officeOptions[number]) ? requestedOffice : "";
  const proof = requestedProof && proofOptions.includes(requestedProof as typeof proofOptions[number]) ? requestedProof : "";
  const unavailableKeys = [
    requestedRoleIds.length > selectedRoleIds.length ? "role" : "",
    requestedJobIds.length > selectedJobIds.length ? "job" : "",
    requestedDomainId && !domainId ? "domain" : "",
    requestedOffice && !office ? "office" : "",
    requestedProof && !proof ? "proof" : ""
  ].filter(Boolean);
  const moments = useMemo(() => workMomentCatalogue(snapshot, {
    query,
    roleIds: selectedRoleIds,
    jobIds: selectedJobIds,
    domainIds: domainId ? [domainId] : [],
    office: office ? [office] : [],
    proof: proof ? [proof] : []
  }), [domainId, selectedJobIds, office, proof, query, selectedRoleIds, snapshot]);
  const momentGroups = snapshot.content.domainWorkblocks.flatMap((domainWorkblock) => {
    const entries = moments.filter((entry) => entry.domainWorkblock.id === domainWorkblock.id);
    return entries.length ? [{ domainWorkblock, entries }] : [];
  });
  const total = snapshot.content.workMoments?.length ?? 0;
  const facetLabels = {
    search: (label: string) => replaceLabel(copy("component.facet.search", "Search {label}"), label),
    clear: (label: string) => replaceLabel(copy("component.facet.clear", "Clear {label}"), label),
    clearAll: copy("component.facet.clear-all", "Clear all"),
    selectedCount: (count: number) => replaceCount(copy("component.facet.selected-count", "{count} selected"), count),
    moreCount: (count: number) => replaceCount(copy("component.facet.more-count", "{count} more"), count),
    noOptions: copy("component.facet.no-options", "No matching options."),
    tryAgain: copy("component.facet.try-again", "Try again."),
    resultCount: (count: number) => replaceCount(copy("component.facet.result-count", "{count} options match."), count),
    optionCount: (count: number) => replaceCount(copy("component.facet.option-count", "{count} matching records"), count),
    unavailableCount: (count: number) => replaceCount(copy("component.facet.unavailable-count", "{count} filters are unavailable in this view."), count),
    clearUnavailable: copy("component.facet.clear-unavailable", "Clear unavailable filters")
  };
  const roleOptions = roles.map(({ definition, placements }) => ({
    id: definition.id,
    label: copy(definition.labelKey),
    context: replaceCount(copy(
      placements.length === 1 ? "catalogue.count.domain.singular" : "catalogue.count.domain.plural",
      placements.length === 1 ? "{count} domain" : "{count} domains"
    ), placements.length),
    count: workMomentCatalogue(snapshot, { roleIds: [definition.id] }).length
  }));
  const jobOptions = jobs.map(({ businessModule, domainWorkblock }) => ({
    id: businessModule.id,
    label: copy(businessModule.labelKey),
    context: copy(domainWorkblock.nameKey),
    count: workMomentCatalogue(snapshot, { jobIds: [businessModule.id] }).length
  }));
  const clearFilters = () => {
    setSearchParams({});
    searchRef.current?.focus();
  };
  const clearUnavailable = () => {
    /* Dropping an unavailable filter keeps every valid selection alive. */
    let next = new URLSearchParams(searchParams);
    for (const key of unavailableKeys) {
      if (key === "role") next = replaceParam(next, "role", selectedRoleIds.join(","));
      else if (key === "job") next = replaceParam(next, "job", selectedJobIds.join(","));
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
    searchRef.current?.focus();
  };
  const updateRole = (ids: readonly string[]) => {
    setSearchParams(replaceParam(searchParams, "role", ids.join(",")));
    if (!ids.length) searchRef.current?.focus();
  };
  const updateJob = (ids: readonly string[]) => {
    setSearchParams(replaceParam(searchParams, "job", ids.join(",")));
    if (!ids.length) searchRef.current?.focus();
  };
  return (
    <div className="apf-page apf-register-reference apf-catalogue-page apf-work-moments-page">
      <PageHeader eyebrow={copy("page.work-moments.eyebrow")} title={copy("page.work-moments.title")} description={copy("page.work-moments.description")} meta={<span>{replaceValues(copy("catalogue.work-moments.count", "{visible} of {total} work moments"), { visible: moments.length, total })}</span>} />
      <PlaceLine section={copy("nav.work-moments")} segments={[{ text: replaceCount(copy("catalogue.count.visible", "{count} visible"), moments.length) }]} cut={replaceValues(copy("catalogue.framework-version", "Framework {version}"), { version: snapshot.frameworkVersion })} />
      <div className="apf-catalogue-controls" role="search" aria-label={copy("catalogue.work-moments.filter-label", "Filter work moments")}>
        <label className="apf-catalogue-control"><span>{copy("catalogue.search", "Search")}</span><input ref={searchRef} type="search" value={query} onChange={(event) => setSearchParams(replaceParam(searchParams, "q", event.target.value), { replace: true })} /></label>
        <label className="apf-catalogue-control"><span>{copy("catalogue.domain", "Domain")}</span><select value={domainId} onChange={(event) => setSearchParams(replaceParam(searchParams, "domain", event.target.value))}><option value="">{copy("catalogue.domain.all", "All domains")}</option>{snapshot.content.domainWorkblocks.map((domain) => <option key={domain.id} value={domain.id}>{copy(domain.nameKey)}</option>)}</select></label>
        <label className="apf-catalogue-control"><span>{copy("catalogue.office", "Office")}</span><select value={office} onChange={(event) => setSearchParams(replaceParam(searchParams, "office", event.target.value))}><option value="">{copy("catalogue.office.any", "Any Office capability")}</option><option value="composer">{copy("office.composer.name", "Composer")}</option><option value="conductor">{copy("office.conductor.name", "Conductor")}</option></select></label>
        <label className="apf-catalogue-control"><span>{copy("catalogue.proof", "Proof")}</span><select value={proof} onChange={(event) => setSearchParams(replaceParam(searchParams, "proof", event.target.value))}><option value="">{copy("catalogue.proof.any", "Any proof level")}</option><option value="reference">{copy("catalogue.proof.reference", "Reference")}</option><option value="pattern">{copy("catalogue.proof.pattern", "Pattern")}</option><option value="roadmap">{copy("catalogue.proof.roadmap", "Roadmap")}</option></select></label>
        {[query, selectedRoleIds.length, selectedJobIds.length, domainId, office, proof].some(Boolean) ? <button className="apf-button apf-button--quiet" type="button" onClick={clearFilters}>{copy("catalogue.clear-filters", "Clear filters")}</button> : null}
      </div>
      <div className="apf-work-moment-facets">
        <CatalogueFacet id="work-moment-role" label={copy("catalogue.role", "Role")} labels={facetLabels} options={roleOptions} selectedIds={selectedRoleIds} onSelectedIdsChange={updateRole} open={roleFacetOpen} onOpenChange={setRoleFacetOpen} unavailableSelectedCount={requestedRoleIds.length - selectedRoleIds.length} onClearUnavailable={() => { setSearchParams(replaceParam(searchParams, "role", selectedRoleIds.join(",")), { replace: true }); searchRef.current?.focus(); }} onClearAll={clearFilters} />
        <CatalogueFacet id="work-moment-job" label={copy("catalogue.job", "Job")} labels={facetLabels} options={jobOptions} selectedIds={selectedJobIds} onSelectedIdsChange={updateJob} open={jobFacetOpen} onOpenChange={setJobFacetOpen} unavailableSelectedCount={requestedJobIds.length - selectedJobIds.length} onClearUnavailable={() => { setSearchParams(replaceParam(searchParams, "job", selectedJobIds.join(",")), { replace: true }); searchRef.current?.focus(); }} onClearAll={clearFilters} />
      </div>
      {unavailableKeys.length ? (
        <div className="apf-catalogue-filter-recovery" role="status">
          <p>{replaceCount(copy(unavailableKeys.length === 1 ? "catalogue.unavailable.singular" : "catalogue.unavailable.plural", unavailableKeys.length === 1 ? "{count} filter is unavailable in this view and does not affect these results." : "{count} filters are unavailable in this view and do not affect these results."), unavailableKeys.length)}</p>
          <button className="apf-button apf-button--quiet" type="button" onClick={clearUnavailable}>{copy("component.facet.clear-unavailable")}</button>
        </div>
      ) : null}
      {moments.length ? <ol className="apf-story-gallery" aria-label={copy("page.work-moments.title")}>{momentGroups.flatMap(({ domainWorkblock, entries }) => entries.map(({ definition, businessModule, perspective, agent, fixture }) => {
        const claim = snapshot.differentiators.claims.find(({ id }) => id === definition.primaryDifferentiatorClaimId);
        const role = roles.find(({ definition: candidate }) => candidate.id === perspective.roleId)?.definition;
        const roleLabel = role ? copy(role.labelKey) : humanize(perspective.roleId);
        /* The card's thumbnail is the decision trace in miniature: the
         * signal's state, the agent's proposal and the committed outcome,
         * every word from the governed fixture. */
        const records = Object.values(fixture.modes).flatMap((fixtureMode) => fixtureMode?.records ?? []);
        const signalState = records.find(({ state }) => state === "exception")?.state ?? records[0]?.state;
        const traceStates = [signalState, records.some(({ state }) => state === "proposal") ? "proposal" : undefined, records.some(({ state }) => state === "committed") ? "committed" : undefined].filter((state): state is string => Boolean(state));
        return (
          <li className="apf-story-card" key={definition.id}>
            <Link
              className="apf-story-card__link"
              to={workMomentPath(definition.id, perspective.roleId)}
              state={{ catalogue: "work-moments", returnTo: `/reference/work-moments${searchParams.toString() ? `?${searchParams.toString()}` : ""}` }}
            >
              {/* The story identity leads; who holds it and how it is
                * delivered follow as two quiet groups. The full job story,
                * agent mission and Office sequence live on the detail page,
                * not in the story card. */}
              <span className="apf-work-moment-index__identity">
                <span className="apf-story-card__domain">{copy(domainWorkblock.nameKey)}</span>
                <span className="apf-proof-line"><span>{copy("work-moment.proof.illustrative")}</span><span>{copy("work-moment.proof.synthetic")}</span></span>
                <span className="apf-story-card__trace">
                  {traceStates.map((state, index) => (
                    <span key={state} className="apf-story-card__trace-step">
                      {index ? <span className="apf-story-card__trace-arrow" aria-hidden="true">→</span> : null}
                      <span className={`apf-story-card__trace-state apf-story-card__trace-state--${state}`}>{copy(`work-moment.state.${state}`, humanize(state))}</span>
                    </span>
                  ))}
                </span>
                <strong className="apf-work-moment-index__title">{copy(definition.titleKey)}</strong>
                <span className="apf-work-moment-index__summary">{copy(definition.summaryKey)}</span>
              </span>
              <span className="apf-work-moment-index__role-world">
                <span className="apf-work-moment-index__role-situation"><strong>{roleLabel}</strong></span>
                <span className="apf-work-moment-index__progress">{copy(perspective.responsibilityKey)}</span>
              </span>
              <span className="apf-work-moment-index__delivery">
                <span className="apf-catalogue-index__meta apf-work-moment-index__agent">{copy(businessModule.labelKey)} · {agent.label}</span>
                <span className="apf-catalogue-index__meta apf-work-moment-index__modes">{definition.availableModes.map((candidate) => modeLabel(candidate, copy)).join(" · ")} · {maturityLabel(claim?.maturity ?? businessModule.implementationStatus, copy)}</span>
              </span>
            </Link>
          </li>
        );
      }))}</ol> : <EmptyState title={copy("catalogue.work-moments.empty.title", "No work moments match")} description={copy("catalogue.work-moments.empty.description", "Clear a filter or browse another role, job or domain.")} action={<button className="apf-button" type="button" onClick={clearFilters}>{copy("catalogue.clear-filters", "Clear filters")}</button>} />}
    </div>
  );
}

function modeLabel(mode: string, copy: (key: string, fallback?: string) => string): string {
  return copy(`work-moment.mode.${mode}.label`, humanize(mode));
}

/** Maturity tokens are machine values; running text carries their governed labels. */
function maturityLabel(token: string, copy: (key: string, fallback?: string) => string): string {
  return copy(`status.${token}.chip`, humanize(token));
}

function outcomeStateForRecord(state: string): SupervisionOutcomeState {
  if (
    state === "committed" ||
    state === "committing" ||
    state === "blocked-by-authority" ||
    state === "failed" ||
    state === "reversed" ||
    state === "proposal" ||
    state === "awaiting-review"
  ) return state;
  return "awaiting-review";
}

function supervisionOutcome(records: readonly { id: string; labelKey: string; state: string; detailKey: string; ownerRoleId: string; evidenceRecordIds: string[] }[], copy: (key: string, fallback?: string) => string, roleLabel: (roleId: string) => string) {
  const recordViews: SupervisionRecordView[] = records.map((record) => ({
    id: record.id,
    label: copy(record.labelKey),
    detail: copy(record.detailKey),
    state: record.state,
    stateLabel: humanize(record.state),
    outcomeState: outcomeStateForRecord(record.state),
    owner: roleLabel(record.ownerRoleId),
    evidenceRecordIds: record.evidenceRecordIds
  }));
  const distinctStates = [...new Set(recordViews.map(({ state }) => state))];
  const uniformOutcomeState = distinctStates.length === 1 ? outcomeStateForRecord(distinctStates[0]!) : "awaiting-review";
  return {
    outcomeState: uniformOutcomeState,
    outcomeStateLabel: distinctStates.length === 1
      ? copy(`work-moment.state.${distinctStates[0]}`, humanize(distinctStates[0]!))
      : replaceCount(copy("work-moment.state.mixed", "Mixed states · {count} records"), recordViews.length),
    records: recordViews
  };
}

export function WorkMomentDetailPage() {
  const { momentId = "" } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const location = useLocation();
  const returnState = location.state as { catalogue?: unknown; returnTo?: unknown } | null;
  const fromWorkMomentsCatalogue = (() => {
    if (returnState?.catalogue !== "work-moments" || typeof returnState.returnTo !== "string") return "/reference/work-moments";
    try {
      const url = new URL(returnState.returnTo, "https://groundworks.local");
      return url.origin === "https://groundworks.local" && url.pathname === "/reference/work-moments"
        ? `${url.pathname}${url.search}${url.hash}`
        : "/reference/work-moments";
    } catch {
      return "/reference/work-moments";
    }
  })();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRoleId = searchParams.get("role") ?? undefined;
  const resolved = resolveWorkMoment(snapshot, momentId, selectedRoleId);
  const [mode, setMode] = useState<string | undefined>(undefined);
  if (!resolved) return <div className="apf-page apf-register-reference"><AbsenceBlock eyebrow={copy("absence.work-moment.eyebrow", "Work moment reference")} title={copy("work-moment.not-found.title", "Work moment not found")} support={copy("work-moment.not-found.description", "This work moment is not part of the current reference.")} headingLevel={1} holds={[{ label: copy("absence.holding.work-moments", "Work moments"), count: snapshot.content.workMoments?.length ?? 0 }, { label: copy("absence.holding.roles", "Roles"), count: roleDefinitions(snapshot).length }, { label: copy("absence.holding.business-modules", "Business modules"), count: snapshot.businessModuleRegistry.businessModules.length }]} routes={<Link to="/reference/work-moments">{copy("work-moment.not-found.action", "Return to work moments")}</Link>} /></div>;
  const { definition, businessModule, domainWorkblock, perspective, agent, fixture } = resolved;
  const roles = roleDefinitions(snapshot);
  const role = roles.find(({ definition: candidate }) => candidate.id === perspective.roleId);
  const roleLabel = (roleId: string) => {
    const canonicalRole = roles.find(({ definition: candidate }) => candidate.id === roleId)?.definition;
    return canonicalRole ? copy(canonicalRole.labelKey) : humanize(roleId);
  };
  const roleReference = (roleId: string) => <Link to={rolePath(roleId)}>{roleLabel(roleId)}</Link>;
  const placement = role?.placements.find(({ domainWorkblock: candidate }) => candidate.id === domainWorkblock.id)?.role;
  const claim = snapshot.differentiators.claims.find(({ id }) => id === definition.primaryDifferentiatorClaimId);
  const moduleStatus = snapshot.content.businessModuleStatusDisplay.find(({ id }) => id === businessModule.implementationStatus);
  const moduleStatusLabel = moduleStatus ? copy(moduleStatus.chipKey) : humanize(businessModule.implementationStatus);
  const activeMode = mode && definition.availableModes.includes(mode as typeof definition.defaultMode) ? mode : definition.defaultMode;
  const story = businessModule.jobStories.find(({ id }) => id === perspective.jobId);
  const evidenceViews = fixture.evidenceRecords.map((record) => ({
    id: record.id,
    label: copy(record.labelKey),
    definition: copy(record.definitionKey),
    owner: roleLabel(record.ownerRoleId),
    source: copy(record.sourceKey),
    provenance: copy(`work-moment.provenance.${record.provenance}`, humanize(record.provenance)),
    freshness: copy(`work-moment.freshness.${record.freshness}`, humanize(record.freshness)),
    observedAt: new Date(record.observedAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" }) + " UTC"
  }));
  const evidenceSummary = fixture.evidenceRecords.map((record) => `${copy(record.labelKey)}: ${copy(`work-moment.freshness.${record.freshness}`, humanize(record.freshness))}`).join(" · ");
  const grantRequirement = businessModule.autonomy.authorityEnvelope.find((item) => /grantor|grant|expiry|effective window/iu.test(item));
  const authoritySource = placement ? `${copy(placement.commitKey)} ${copy(placement.aloneKey)}` : copy(perspective.responsibilityKey);
  const authority = {
    summary: authoritySource,
    source: `${roleLabel(perspective.roleId)} · ${copy(businessModule.labelKey)}`,
    posture: copy(`work-moment.autonomy.${businessModule.autonomy.targetPosture}`, humanize(businessModule.autonomy.targetPosture)),
    grantor: roleReference(fixture.authorityGrantorRoleId),
    grantRequirement,
    allowedActionClasses: agent.allowedActionClasses,
    interventionRights: copy(perspective.supervisionKey),
    humanSupervision: businessModule.autonomy.humanSupervision,
    escalationTriggers: businessModule.autonomy.alwaysEscalate,
    escalationOwner: roleReference(fixture.escalationOwnerRoleId),
    recovery: businessModule.autonomy.rollback
  };
  const fixtureState = fixture.evidenceRecords.some(({ freshness }) => freshness === "stale")
    ? "stale"
    : fixture.evidenceRecords.some(({ freshness }) => freshness === "conflicting")
      ? "partial"
      : "ready";
  const modes: SupervisionModeView[] = definition.availableModes.map((modeId) => {
    const fixtureMode = fixture.modes[modeId];
    const records = fixtureMode?.records ?? [];
    const orderedOfficeArtifacts = modeId === "open-office-artifact"
      ? (definition.officeSequence ?? []).flatMap((product) => {
          const artifact = fixture.officeArtifacts?.find((candidate) => candidate.product === product);
          return artifact ? [artifact] : [];
        })
      : [];
    const outcome = supervisionOutcome(records, copy, roleLabel);
    const learningRecord = modeId === "review-learning" ? records[0] : undefined;
    return {
      id: modeId,
      label: modeLabel(modeId, copy),
      question: copy(`work-moment.mode.${modeId}.question`),
      primary: fixtureMode ? `${copy(fixtureMode.headingKey)}. ${copy(fixtureMode.summaryKey)}` : copy(perspective.supervisionKey),
      evidence: evidenceSummary,
      evidenceRecords: evidenceViews,
      authority,
      outcomeState: outcome.outcomeState,
      outcomeStateLabel: outcome.outcomeStateLabel,
      outcome: records.length ? replaceCount(copy(records.length === 1 ? "work-moment.outcome.records.singular" : "work-moment.outcome.records.plural", records.length === 1 ? "{count} record retains its individual state below." : "{count} records retain their individual state below."), records.length) : copy(perspective.nowKey),
      records: outcome.records,
      learning: learningRecord ? {
        evaluation: copy(learningRecord.detailKey),
        proposedAdaptation: copy(fixtureMode?.summaryKey ?? perspective.nowKey),
        releaseOwner: roleLabel(learningRecord.ownerRoleId),
        rollback: businessModule.autonomy.rollback
      } : undefined,
      officeHandoff: orderedOfficeArtifacts.length ? {
        steps: orderedOfficeArtifacts.map((officeArtifact) => ({
          product: officeArtifact.product,
          productLabel: copy(`office.${officeArtifact.product}.name`, humanize(officeArtifact.product)),
          label: copy(officeArtifact.labelKey),
          artifactType: copy(officeArtifact.artifactTypeKey),
          reviewedVersion: copy(officeArtifact.reviewedVersionKey),
          scope: copy(officeArtifact.scopeKey),
          proposal: copy(officeArtifact.proposalKey),
          readBackResult: copy(officeArtifact.returnRecordKey),
          state: officeArtifact.state,
          stateLabel: copy(`work-moment.state.${officeArtifact.state}`, humanize(officeArtifact.state)),
          to: officeCapabilityPath(officeArtifact.product),
          linkLabel: replaceLabel(copy("work-moment.office.open", "Open {label}"), copy(`office.${officeArtifact.product}.name`, humanize(officeArtifact.product)))
        })),
        returnLabel: copy(businessModule.labelKey),
        returnRecord: records.length ? records.map((record) => copy(record.detailKey)).join(" · ") : copy(perspective.nowKey),
        returnState: outcome.outcomeState,
        returnStateLabel: outcome.outcomeStateLabel
      } : undefined
    };
  });
  return (
    <div className="apf-page apf-register-marketing apf-work-moment-detail">
      <Link className="apf-button apf-button--quiet apf-back-link" to={fromWorkMomentsCatalogue}>{copy("work-moment.back", "Back to work moments")}</Link>
      <PlaceTrail stops={[
        { label: copy("component.place-trail.framework", "Framework"), to: "/" },
        { label: copy("nav.work-moments", "Work moments"), to: "/reference/work-moments" },
        { label: copy(definition.titleKey) }
      ]} />
      <div className="apf-scene-hero">
        <PageHeader eyebrow={`${copy("work-moment.proof.illustrative")} · ${copy("work-moment.proof.synthetic")}`} title={copy(definition.titleKey)} description={copy(definition.summaryKey)} meta={<span>{copy(domainWorkblock.nameKey)}</span>} />
      </div>
      {definition.perspectives.length > 1 ? (
        <div className="apf-perspective-tabs" role="group" aria-label={copy("work-moment.perspective", "Role perspective")}>
          <span className="apf-perspective-tabs__label">{copy("work-moment.perspective", "Role perspective")}</span>
          {definition.perspectives.map((candidate) => {
            const candidateRole = roles.find(({ definition: item }) => item.id === candidate.roleId);
            const label = candidateRole ? copy(candidateRole.definition.labelKey) : humanize(candidate.roleId);
            const active = candidate.roleId === perspective.roleId;
            return (
              <button key={candidate.roleId} type="button" className="apf-perspective-tab" aria-pressed={active} onClick={() => setSearchParams({ role: candidate.roleId })}>
                {label}
              </button>
            );
          })}
        </div>
      ) : null}
      <WorkMomentIllustration momentId={definition.id} roleId={perspective.roleId} variant="full" />
      <section className="apf-work-moment-story" aria-label={copy("work-moment.story.label", "How the work changes")}><dl className="apf-definition-list"><div><dt>{copy("work-moment.story.role", "Role and situation")}</dt><dd>{role ? <Link to={rolePath(role.definition.id)}>{copy(role.definition.labelKey)}</Link> : humanize(perspective.roleId)} · {copy(perspective.responsibilityKey)}</dd></div><div><dt>{copy("work-moment.story.progress", "Progress")}</dt><dd>{story ? copy(story.storyKey) : copy(businessModule.jobStatementKey)}</dd></div><div><dt>{copy("work-moment.story.finish", "Observable finish")}</dt><dd>{story ? copy(story.completionKey) : copy(businessModule.outcomeKey)}</dd></div><div><dt>{copy("work-moment.story.before", "Before")}</dt><dd>{copy(perspective.beforeKey)}</dd></div><div><dt>{copy("work-moment.story.now", "Now")}</dt><dd>{copy(perspective.nowKey)}</dd></div><div><dt>{copy("work-moment.story.coexistence", "Coexistence")}</dt><dd>{copy(definition.coexistenceKey)}</dd></div></dl></section>
      <ModuleAgentSupervisionFrame
        labels={{
          illustrative: copy("work-moment.proof.illustrative", "Illustrative work moment"),
          synthetic: copy("work-moment.proof.synthetic", "Synthetic data"),
          modeLabel: copy("work-moment.frame.mode-label", "Work moment mode"),
          roleSituation: copy("work-moment.frame.role-situation", "Role situation"),
          agentMission: copy("work-moment.frame.agent-mission", "Module agent mission"),
          evidence: copy("work-moment.frame.evidence", "Evidence and freshness"),
          authority: copy("work-moment.frame.authority", "Authority and intervention"),
          outcomeOwner: copy("work-moment.frame.outcome-owner", "Outcome owner"),
          outcome: copy("work-moment.frame.outcome", "Outcome state"),
          learning: copy("work-moment.frame.learning", "Learning decision"),
          officeHandoff: copy("work-moment.frame.office-handoff", "Office handoff"),
          moduleStatus: copy("work-moment.frame.module-status", "Module status"),
          proofMaturity: copy("work-moment.frame.proof-maturity", "Differentiator maturity")
          , evidenceOwner: copy("work-moment.frame.evidence-owner", "Evidence owner")
          , evidenceSource: copy("work-moment.frame.evidence-source", "Source")
          , provenance: copy("work-moment.frame.provenance", "Provenance")
          , freshness: copy("work-moment.frame.freshness", "Freshness")
          , observedAt: copy("work-moment.frame.observed-at", "Observed")
          , recordOwner: copy("work-moment.frame.record-owner", "Record owner")
          , authoritySource: copy("work-moment.frame.authority-source", "Authority source")
          , authorityPosture: copy("work-moment.frame.authority-posture", "Autonomy posture")
          , grantor: copy("work-moment.frame.grantor", "Authority grantor")
          , actionScope: copy("work-moment.frame.action-scope", "Allowed action classes")
          , interventionRights: copy("work-moment.frame.intervention-rights", "Intervention rights")
          , humanSupervision: copy("work-moment.frame.human-supervision", "Human supervision")
          , escalation: copy("work-moment.frame.escalation", "Always escalate")
          , escalationOwner: copy("work-moment.frame.escalation-owner", "Escalation owner")
          , recovery: copy("work-moment.frame.recovery", "Recovery")
          , learningEvaluation: copy("work-moment.frame.learning-evaluation", "Evaluation result")
          , learningProposal: copy("work-moment.frame.learning-proposal", "Reviewed adaptation proposal")
          , releaseOwner: copy("work-moment.frame.release-owner", "Release owner")
          , rollback: copy("work-moment.frame.rollback", "Rollback")
          , artifact: copy("work-moment.frame.artifact", "Artefact")
          , reviewedVersion: copy("work-moment.frame.reviewed-version", "Reviewed version")
          , scope: copy("work-moment.frame.scope", "Native scope")
          , proposedMutation: copy("work-moment.frame.proposed-mutation", "Proposed mutation")
          , readBackResult: copy("work-moment.frame.read-back-result", "Read-back result")
          , workblockReturn: copy("work-moment.frame.workblock-return", "Workblock return")
        }}
        role={{ label: role ? copy(role.definition.labelKey) : humanize(perspective.roleId), situation: copy(perspective.responsibilityKey), outcomeOwner: role ? copy(role.definition.labelKey) : humanize(perspective.roleId) }}
        agent={{ label: agent.label, mission: copy(agent.contributionKey) }}
        moduleStatus={moduleStatusLabel}
        proofMaturity={claim?.maturity ?? businessModule.implementationStatus}
        mode={activeMode}
        modes={modes}
        onModeChange={setMode}
        fixtureState={fixtureState}
        fixtureStateLabel={fixtureState === "stale" ? copy("work-moment.fixture.stale", "Stale evidence retained") : fixtureState === "partial" ? copy("work-moment.fixture.partial", "Conflicting evidence retained") : undefined}
        proofClaim={claim?.statement}
      />
      <section className="apf-work-moment-print-modes" aria-label={copy("work-moment.print.label", "Complete Work moment reference")}>{printModeOrder.flatMap((modeId) => { const printMode = modes.find(({ id }) => id === modeId); return printMode ? [<article key={modeId}><h2>{printMode.label}</h2><h3>{printMode.question}</h3><div>{printMode.primary}</div><h4>{copy("work-moment.frame.evidence")}</h4><div>{printMode.evidence}</div>{printMode.evidenceRecords?.map((record) => <dl key={record.id}><div><dt>{record.label}</dt><dd>{record.definition}</dd></div><div><dt>{copy("work-moment.frame.evidence-owner")}</dt><dd>{record.owner}</dd></div><div><dt>{copy("work-moment.frame.evidence-source")}</dt><dd>{record.source}</dd></div><div><dt>{copy("work-moment.frame.provenance")}</dt><dd>{record.provenance}</dd></div><div><dt>{copy("work-moment.frame.freshness")}</dt><dd>{record.freshness}</dd></div><div><dt>{copy("work-moment.frame.observed-at")}</dt><dd>{record.observedAt}</dd></div></dl>)}<h4>{copy("work-moment.frame.authority")}</h4><div>{printMode.authority.summary}</div><dl><div><dt>{copy("work-moment.frame.authority-source")}</dt><dd>{printMode.authority.source}</dd></div><div><dt>{copy("work-moment.frame.authority-posture")}</dt><dd>{printMode.authority.posture}</dd></div><div><dt>{copy("work-moment.frame.grantor")}</dt><dd>{printMode.authority.grantor}</dd></div><div><dt>{copy("work-moment.frame.escalation-owner")}</dt><dd>{printMode.authority.escalationOwner}</dd></div><div><dt>{copy("work-moment.frame.intervention-rights")}</dt><dd>{printMode.authority.interventionRights}</dd></div></dl>{printMode.authority.grantRequirement ? <p>{printMode.authority.grantRequirement}</p> : null}<h5>{copy("work-moment.frame.action-scope")}</h5><ul>{printMode.authority.allowedActionClasses.map((item, index) => <li key={index}>{item}</li>)}</ul><h5>{copy("work-moment.frame.human-supervision")}</h5><ul>{printMode.authority.humanSupervision.map((item, index) => <li key={index}>{item}</li>)}</ul><h5>{copy("work-moment.frame.escalation")}</h5><ul>{printMode.authority.escalationTriggers.map((item, index) => <li key={index}>{item}</li>)}</ul><h5>{copy("work-moment.frame.recovery")}</h5><ul>{printMode.authority.recovery.map((item, index) => <li key={index}>{item}</li>)}</ul><h4>{copy("work-moment.frame.outcome")}</h4><p><strong>{printMode.outcomeStateLabel}</strong> · {printMode.outcome}</p>{printMode.records?.map((record) => <p key={record.id}><strong>{record.label}</strong> · {record.stateLabel} · {record.owner}: {record.detail}</p>)}{printMode.learning ? <section><h4>{copy("work-moment.frame.learning")}</h4><dl><div><dt>{copy("work-moment.frame.learning-evaluation")}</dt><dd>{printMode.learning.evaluation}</dd></div><div><dt>{copy("work-moment.frame.learning-proposal")}</dt><dd>{printMode.learning.proposedAdaptation}</dd></div><div><dt>{copy("work-moment.frame.release-owner")}</dt><dd>{printMode.learning.releaseOwner}</dd></div></dl><h5>{copy("work-moment.frame.rollback")}</h5><ul>{printMode.learning.rollback.map((item, index) => <li key={index}>{item}</li>)}</ul></section> : null}{printMode.officeHandoff && "steps" in printMode.officeHandoff ? <ol>{printMode.officeHandoff.steps.map((step) => <li key={step.product}><strong>{step.productLabel}</strong> · {step.readBackResult}</li>)}<li><strong>{copy("work-moment.frame.workblock-return")}</strong> · {printMode.officeHandoff.returnStateLabel} · {printMode.officeHandoff.returnRecord}</li></ol> : null}</article>] : []; })}</section>
      <section className="apf-page-section" aria-labelledby="moment-proof-title"><h2 id="moment-proof-title">{copy("work-moment.proof.heading", "What this moment proves")}</h2><p>{copy(perspective.nowKey)}</p>{claim?.statement ? <p>{claim.statement}</p> : null}<p><StatusMark value={businessModule.implementationStatus} /> · {copy("work-moment.proof.differentiator", "Differentiator")}: {claim?.maturity ?? businessModule.implementationStatus}</p><p><Link to={businessModulePath(domainWorkblock.id, businessModule.id)}>{replaceLabel(copy("work-moment.job.open", "Open the {label} job"), copy(businessModule.labelKey))}</Link></p></section>
    </div>
  );
}
